import React, { FC, useEffect, useMemo, useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Divider, Grid, Tab, Tabs, Button, Box } from "@mui/material";
import { useParams } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { styles } from "../styles";
import { useSample } from "../../utils/useSample";
import useAnalysis from "../../utils/useAnalysis";
import useTabs from "../../utils/useTab";
import { MAutocomplete } from "@app/components";
import { useEquipmentMide } from "@services/servicesGenericsTables";
import ErrorMessage from "@app/utils/ErrorMessage";
import { mapTabsNav } from "./utils/contants";
import { useIsAllSaved } from "./utils/helpers";
import CopyAnalysisDataModal from "../modals/CopyAnalysisDataModal";
import { AnalysisType, KeysTabsAnalysis } from "@app/types/samples.types";
import { successAlert, errorAlert } from "@app/utils/alerts";
import { postData, getData } from "@services/utils/formHelpers";

const PhasesAnalysis: FC = () => {
  const { tabValueAnalysis, setTabValueAnalysis } = useTabs();
  const { watch, setValue, getValues } = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample, get, getAndSet, fetchAll, fetchOne } = useSample();
  const { fetchByAnalysisType, loading } = useEquipmentMide();
  const sample = get(Number(idMuestra));
  const { setProcessingId, resetInternalState, fetchPreparations } = useAnalysis();
  const [copyModalOpen, setCopyModalOpen] = useState(false);

  const typeAnalysis = watch("typeAnalysis");

  useEffect(() => {
    setTabValueAnalysis(0);
  }, []);

  useEffect(() => {
    setValue("typePreparation", null);
    const fetchData = async () => {
      await fetchByAnalysisType(typeAnalysis?.id);
    };
    if (typeAnalysis) {
      resetInternalState();
      fetchData();
    }
  }, [JSON.stringify(typeAnalysis)]);

  const allSaved = useIsAllSaved(stateSample, setProcessingId, idMuestra);

  const mapKey = `${typeAnalysis?.codigo}`;
  const ComponentSelected = mapTabsNav[mapKey]?.form as FC;

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValueAnalysis(newValue);
  };

  const handleOpenCopyModal = () => {
    console.log("Abriendo modal de copia de datos");
    console.log("Tipo de análisis actual:", typeAnalysis);
    console.log("Tipos disponibles:", sample?.analysis_type);
    setCopyModalOpen(true);
  };

  const handleCloseCopyModal = () => {
    setCopyModalOpen(false);
  };

  const handleCopyData = async (targetAnalysisType: AnalysisType) => {
    try {
      // Obtener el código del tipo de análisis actual y objetivo
      const sourceCode = typeAnalysis.codigo as KeysTabsAnalysis;
      const targetCode = targetAnalysisType.codigo as KeysTabsAnalysis;
      const sourceDescription = typeAnalysis.descripcion;

      const response = await postData("/preparation/copy-structure", {
        sample_id: Number(idMuestra),
        source_analysis_type: sourceCode,
        destination_analysis_type: targetCode,
      });

      if (!response?.success) {
        errorAlert((response as any)?.message || "Error al copiar los datos");
        return;
      }
      
      let createdPreparationIds = response.data.preparation_ids || [];
      let deletedPreparationIds = response.data.deleted_preparation_ids || [];

      console.log(`Preparaciones creadas: ${createdPreparationIds.length}`);
      console.log(`Preparaciones eliminadas: ${deletedPreparationIds.length}`);

      const { preparationsList: currentPrepsList, fetchPreparations, setPreparationsList } = useAnalysis.getState();

      // Si se eliminaron preparaciones, actualizar el store para reflejar esto
      if (deletedPreparationIds.length > 0) {
        const updatedPrepsList = currentPrepsList.filter(
          (prep) => !deletedPreparationIds.includes(prep.id)
        );
        setPreparationsList(updatedPrepsList);
        console.log("Preparaciones eliminadas del store:", deletedPreparationIds);
      }

      // Cerrar el modal primero
      handleCloseCopyModal();

      // Cargar las preparaciones recién creadas directamente usando sus IDs
      console.log("Cargando preparaciones creadas desde el backend...");
      const newPreparations = createdPreparationIds.map(id => ({ id }));
      
      // Obtener las preparaciones actuales del store y agregar las nuevas
      const { preparationsList: latestPrepsList } = useAnalysis.getState();
      const combinedPrepsList = [...latestPrepsList, ...newPreparations];
      
      // Cargar los detalles completos de las nuevas preparaciones
      // fetchPreparations solo usa el campo id internamente
      
      await fetchPreparations(combinedPrepsList as any);
      
      // Esperar a que se actualice el store
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Cambiar al tipo de análisis objetivo
      setValue("typeAnalysis", targetAnalysisType);

      // Esperar a que React actualice el estado
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Obtener las preparaciones actualizadas del store
      const { 
        preparationsList: updatedPrepsList,
        setCurrentPreparation,
        setCurrentMeasurement,
        setCurrentCounting,
        setCurrentCalculation
      } = useAnalysis.getState();

      console.log("Total preparaciones en store:", updatedPrepsList.length);

      // Filtrar las preparaciones por el tipo de análisis destino
      const filteredPreps = updatedPrepsList.filter((prep) =>
        prep.analysis_type.some((analysis: any) => analysis.id === targetAnalysisType.id)
      );

      console.log("Preparaciones filtradas para el tipo destino:", filteredPreps.length);

      // Establecer la primera preparación creada como actual
      if (filteredPreps.length > 0 && createdPreparationIds.length > 0) {
        // Buscar la primera preparación creada en la lista filtrada
        const firstNewPrepIndex = filteredPreps.findIndex(
          (prep) => prep.id === createdPreparationIds[0]
        );

        const indexToUse = firstNewPrepIndex !== -1 ? firstNewPrepIndex : 0;
        const newPrep = filteredPreps[indexToUse];
        
        console.log("Estableciendo preparación actual:", newPrep.id, "índice:", indexToUse);
        setCurrentPreparation(newPrep.id, indexToUse, true);

        if (newPrep.aa_measures && newPrep.aa_measures.length > 0) {
          const firstMeasure = newPrep.aa_measures[0];
          console.log("Estableciendo medición actual:", firstMeasure.id);
          setCurrentMeasurement(firstMeasure.id, 0, true);

          if (firstMeasure.countings && firstMeasure.countings.length > 0) {
            const firstCounting = firstMeasure.countings[0];
            console.log("Estableciendo contaje actual:", firstCounting.id);
            setCurrentCounting(firstCounting.id, 0, true);

            if (firstCounting.calculations && firstCounting.calculations.length > 0) {
              console.log("Estableciendo cálculo actual:", firstCounting.calculations[0].id);
              setCurrentCalculation(firstCounting.calculations[0].id, 0);
            }
          }
        } else if (newPrep.countings && newPrep.countings.length > 0) {
          const firstCounting = newPrep.countings[0];
          console.log("Estableciendo contaje actual (directo):", firstCounting.id);
          setCurrentCounting(firstCounting.id, 0, true);

          if (firstCounting.calculations && firstCounting.calculations.length > 0) {
            console.log("Estableciendo cálculo actual:", firstCounting.calculations[0].id);
            setCurrentCalculation(firstCounting.calculations[0].id, 0);
          }
        }
      }

      // Forzar actualización del formulario cambiando y volviendo a establecer el tipo
      // Esto fuerza a React a re-renderizar con los nuevos datos
      setValue("typeAnalysis", null);
      await new Promise((resolve) => setTimeout(resolve, 100));
      setValue("typeAnalysis", targetAnalysisType);

      successAlert(
        `Se copiaron ${createdPreparationIds.length} preparación(es) de "${sourceDescription}" a "${targetAnalysisType.descripcion}".`
      );

    } catch (error) {
      console.error("Error al copiar datos:", error);
      errorAlert("Hubo un error al copiar los datos. Por favor, intente nuevamente.");
    }
  };

  return (
    <>
      {allSaved ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2} mt={2}>
            <MAutocomplete
              key={JSON.stringify(getValues("typeAnalysis"))}
              name="typeAnalysis"
              options={sample?.analysis_type || []}
              label="Tipo de análisis"
              getOptionLabel={(option) => option.descripcion}
              validation={{ required: "El tipo de análisis es requerido" }}
            />
          </Grid>

          {!!typeAnalysis && ['BT', 'BR', 'Sr89', 'Sr90'].includes(typeAnalysis.codigo) && (
            <Grid item xs={12} sm={2} mt={2} display="flex" alignItems="center">
              <Button
                variant="outlined"
                onClick={handleOpenCopyModal}
                fullWidth
                sx={{ 
                  height: "56px",
                  textTransform: "none",
                  fontSize: "0.95rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                Copiar datos
              </Button>
            </Grid>
          )}

          {!!typeAnalysis && mapTabsNav[typeAnalysis.codigo] && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={false} sm={2} />
                <Grid item xs={12} sm={10}>
                  <Tabs
                    value={tabValueAnalysis}
                    onChange={handleChangeTab}
                    aria-label={`tabs-${typeAnalysis?.descripcion}`}
                  >
                    {typeAnalysis &&
                      mapTabsNav[typeAnalysis.codigo]?.tab?.map((set, index) => (
                        <Tab key={index} label={set.label} />
                      ))}
                  </Tabs>
                  <Divider />
                </Grid>
              </Grid>

              <Grid container sx={styles.mainContainer}>
                <Grid item xs={2} sx={styles.navHistoryContainer}>
                  {mapTabsNav[typeAnalysis.codigo]?.navigator}
                </Grid>

                <Grid item xs={10} mt={2} p={4}>
                  {!loading && <ComponentSelected />}
                </Grid>
              </Grid>
            </>
          )}
          {!!typeAnalysis && !mapTabsNav[typeAnalysis.codigo] && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ErrorMessage message={`El tipo de análisis "${typeAnalysis.descripcion}" (código: ${typeAnalysis.codigo}) no está configurado.`} />
              </Grid>
            </Grid>
          )}
        </Grid>
      ) : (
        <ErrorMessage />
      )}

      {typeAnalysis && (
        <CopyAnalysisDataModal
          open={copyModalOpen}
          onClose={handleCloseCopyModal}
          currentAnalysisType={typeAnalysis}
          availableAnalysisTypes={sample?.analysis_type || []}
          onCopy={handleCopyData}
        />
      )}
    </>
  );
};

export default PhasesAnalysis;
